import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _taggedTemplateLiteralLoose from "@babel/runtime/helpers/esm/taggedTemplateLiteralLoose";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";

function _templateObject() {
  var data = _taggedTemplateLiteralLoose(["\n\t\t\t@media (prefers-reduced-motion) {\n\t\t\t\ttransition: none !important;\n\t\t\t}\n\t\t\t", " & {\n\t\t\t\ttransition: none !important;\n\t\t\t}\n\t\t"]);

  _templateObject = function () {
    return data;
  };

  return data;
}

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import isPropValid from '@emotion/is-prop-valid';
import { mergeRefs } from '@wp-g2/utils';
import React, { forwardRef } from 'react';
import { useHydrateGlobalStyles } from '../hooks';
import { REDUCED_MOTION_MODE_ATTR } from './constants';
import { DEFAULT_STYLE_SYSTEM_OPTIONS } from './utils';
var shouldForwardProp = isPropValid;
var defaultOptions = DEFAULT_STYLE_SYSTEM_OPTIONS;
/**
 * @typedef CreateCoreElementOptions
 * @property {import('create-emotion').ObjectInterpolation<any>} baseStyles The baseStyles from the Style system.
 * @property {import('../create-compiler').Compiler} compiler The injectGlobal from the Style system's compiler.
 * @property {import('./generate-theme').GenerateThemeResults} globalStyles The globalStyles from the Style system.
 */

/**
 * Creates the core styled elements for the Style system. These elements are
 * an in-between of Emotion's <div css="" /> solution and a styled.div``
 * solution.
 *
 * createCoreElement is a super light-weight higher-order wrapper that
 * enhances base elements (like `div`, `input`, or even Components) with
 * features provided by the Style system, such as direct CSS compiling via
 * the `css` prop.
 *
 * A styled element also has built-in baseStyles (which can be adjusted using
 * the createStyleSystem factory).
 *
 * @example
 * ```jsx
 * const alwaysBlueDiv = createCoreElement('div', { baseStyles: { background: 'blue' }})
 * ```
 *
 * @template {keyof JSX.IntrinsicElements} TTagName
 * @param {TTagName} tagName The HTMLElement/React.Component to connect with the Style system.
 * @param {CreateCoreElementOptions} options Options to custom coreElement styles.
 * @returns {import('./polymorphic-component').PolymorphicComponent<TTagName, {}>} The Style system wrapped HTMLElement/React.Component.
 */

export var createCoreElement = function (tagName, options) {
  var _defaultOptions$optio = _objectSpread(_objectSpread({}, defaultOptions), options),
      baseStyles = _defaultOptions$optio.baseStyles,
      compiler = _defaultOptions$optio.compiler,
      globalStyles = _defaultOptions$optio.globalStyles;

  var css = compiler.css,
      cx = compiler.cx,
      injectGlobal = compiler.injectGlobal;
  /**
   * Default baseStyles for the system.
   */

  var styles = {
    Base: css({
      // Automatic box-sizing resets.
      boxSizing: 'border-box'
    }),
    // Enforced reduced-motion preferred styles.
    reduceMotion: css(_templateObject(), REDUCED_MOTION_MODE_ATTR)
  };
  var compiledBaseStyles = css(baseStyles);
  /**
   * @param {any} props
   * @param {import('react').Ref<any>} ref
   */

  var SystemComponent = /*#__PURE__*/forwardRef(function render(_ref, ref) {
    var cssProp = _ref.css,
        as = _ref.as,
        children = _ref.children,
        classNameProp = _ref.className,
        forwardedRef = _ref.forwardedRef,
        props = _objectWithoutProperties(_ref, ["css", "as", "children", "className", "forwardedRef"]);

    /**
     * useHydrateGlobalStyles an incredibly important hook, and is vital
     * to the Style system. It automatically injects the variables Style
     * system configs (configs, dark mode, etc...) on first-render.
     *
     * This way avoids....
     *
     * 1. The need to wrap coreElements / styled components in any <Provider />.
     * 2. The need to use Context connectors (e.g. for ThemeProvider), which is HUGE for performance.
     */
    // eslint-disable-next-line
    useHydrateGlobalStyles({
      globalStyles: globalStyles,
      injectGlobal: injectGlobal
    });
    var element = as || tagName;
    var className = typeof classNameProp !== 'string' ? cx(classNameProp) : classNameProp;
    /**
     * Compiles all of the custom styles into classNames before binding it
     * to the HTMLElement / React.Component.
     */

    var classes = cx(styles.Base, styles.reduceMotion, compiledBaseStyles, className, cssProp && css(cssProp));
    /**
     * A conventient feature to (attempt to) filter out non HTML-friendly
     * props for HTMLElements.
     */

    /** @type {Record<string, any>} */
    var newProps = {};

    for (var key in props) {
      if (typeof element === 'string') {
        if (shouldForwardProp(key)) {
          newProps[key] = props[key];
        }
      } else {
        newProps[key] = props[key];
      }
    } // eslint-disable-next-line


    var refs = React.useMemo(function () {
      return forwardedRef ? mergeRefs([forwardedRef, ref]) : ref;
    }, [forwardedRef, ref]);
    return /*#__PURE__*/React.createElement(element, _objectSpread({
      className: classes,
      ref: refs
    }, newProps), children);
  });

  if (process.env.NODE_ENV === 'development') {
    var displayName = typeof tagName === 'string' ? tagName : 'Component';
    SystemComponent.displayName = displayName;
  }

  return SystemComponent;
};